/**
 * PostHog Service - Singleton service for managing PostHog analytics
 * Note: This system tracks dental professional scheduling - no patient data
 */

import posthog, { PostHog } from 'posthog-js';
import { v4 as uuidv4 } from 'uuid';
import { PosthogConfig, PosthogEventProperties } from './types';
import { sanitizePayload } from '~/utils/posthog/sanitizers';
import { featureFlagManager } from './FeatureFlagManager';
import history from '../../browserHistory';
import { funnelRegistry } from './funnelRegistry';

class PosthogService {
  private static instance: PosthogService;
  private posthogInstance: PostHog | null = null;
  private initialized: boolean = false;
  private flagsReady: boolean = false;
  private flagsReadyPromise: Promise<void> | null = null;
  private flagsReadyResolver: (() => void) | null = null;
  private config: PosthogConfig | null = null;
  private activeFunnels: Map<string, string> = new Map();

  // Tracks the last reported value for each flag key. With
  // send_event: false on SDK calls, we manually fire
  // $feature_flag_called once per flag key per session AND again
  // whenever the resolved value changes (e.g. after a flag reload).
  private flagCallReported: Record<string, boolean | string | null> = {};

  private constructor() {}

  /**
   * Get singleton instance of PosthogService
   */
  static getInstance(): PosthogService {
    if (!PosthogService.instance) {
      PosthogService.instance = new PosthogService();
    }
    return PosthogService.instance;
  }

  /**
   * Get the underlying PostHog instance (for internal use)
   */
  getPosthogInstance(): PostHog | null {
    return this.posthogInstance;
  }

  /**
   * Initialize PostHog with configuration
   */
  initialize(config: PosthogConfig): void {
    // Don't initialize if already initialized or disabled
    if (this.initialized || config.disabled || !config.apiKey) {
      if (config.debug) {
        console.log('[PostHog] Initialization skipped', {
          initialized: this.initialized,
          disabled: config.disabled,
          hasApiKey: !!config.apiKey,
        });
      }
      return;
    }

    this.config = config;

    try {
      posthog.init(config.apiKey, {
        api_host: config.apiHost || 'https://t.gotu.com',
        ui_host: config.uiHost || 'https://us.posthog.com',
        autocapture: false, // We'll track manually via saga
        capture_pageview: false, // Manual page view tracking for better control
        capture_pageleave: false, // Disable automatic page leave tracking
        disable_session_recording: true, // Disable automatic recording - opt-in only for specific flows
        debug: config.debug,
        persistence: 'localStorage+cookie',
        // Cookie is scoped to this subdomain only. Without this, the PostHog cookie is set on
        // the parent domain (e.g. .tempdee.com) and shared with admin-portal, which causes
        // Person records to merge across apps when admins sign into one portal after the other.
        cross_subdomain_cookie: false,
        // Note: config.js 404 is expected until you create feature flags/experiments in PostHog
      });

      posthog.register({
        environment: import.meta.env.VITE_TEMP_MEE_ENV || 'development',
        appType: 'dental_portal',
      });

      // Set as person property for feature flag evaluation (works for anonymous users too)
      posthog.people.set({
        environment: import.meta.env.VITE_TEMP_MEE_ENV || 'development',
        appType: 'dental_portal',
      });

      // Use the global posthog instance directly since init is synchronous
      this.posthogInstance = posthog;
      // Also expose globally for toolbar
      (window as any).posthog = posthog;
      this.initialized = true;

      // Set up promise for feature flags readiness
      this.flagsReadyPromise = new Promise<void>((resolve) => {
        this.flagsReadyResolver = resolve;
      });

      // Set up feature flags ready listener
      this.posthogInstance.onFeatureFlags(() => {
        this.flagsReady = true;
        if (this.flagsReadyResolver) {
          this.flagsReadyResolver();
          this.flagsReadyResolver = null; // Clear resolver after use
        }
        if (config.debug) {
          console.log('[PostHog] Feature flags loaded and ready');
        }
      });

      // Set up history listener for pageview tracking
      this.setupPageViewTracking();
    } catch (error) {
      console.error('[PostHog] Initialization error:', error);
    }
  }

  /**
   * Set up automatic pageview tracking using history
   */
  private setupPageViewTracking(): void {
    // Track initial page view
    this.trackPageView(window.location.pathname, {
      search: window.location.search,
      hash: window.location.hash,
    });

    // Listen for route changes
    history.listen((update) => {
      const pathname = update.location ? update.location.pathname : window.location.pathname;
      const search = update.location ? update.location.search : window.location.search;
      const hash = update.location ? update.location.hash : window.location.hash;

      this.trackPageView(pathname, {
        search,
        hash,
      });
    });
  }

  /**
   * Identify a user with PostHog
   */
  identify(userId: string, traits?: Record<string, any>): void {
    if (!this.isReady()) return;

    try {
      // Only sanitize auth tokens, preserve all user data
      const sanitizedTraits = sanitizePayload(traits || {});
      this.posthogInstance?.identify(userId, sanitizedTraits);

      if (this.config?.debug) {
        console.log('[PostHog] User identified:', userId, sanitizedTraits);
      }
    } catch (error) {
      console.error('[PostHog] Identify error:', error);
    }
  }

  /**
   * Track an event with PostHog
   */
  track(event: string, properties?: PosthogEventProperties): void {
    if (!this.isReady()) return;

    try {
      // Enrich with funnel tracking ID if applicable
      const enrichedProperties = this.enrichWithFunnelId(event, properties);
      // Only sanitize auth tokens, preserve all business data
      const sanitizedProperties = sanitizePayload(enrichedProperties);
      this.posthogInstance?.capture(event, sanitizedProperties);

      if (this.config?.debug) {
        console.log('[PostHog] Event tracked:', event, sanitizedProperties);
      }
    } catch (error) {
      console.error('[PostHog] Track error:', error);
    }
  }

  /**
   * Enriches event properties with funnelEntryId based on funnel registry.
   * - Entry events generate a new UUID and store it for the funnel
   * - Funnel events receive the stored UUID from their associated funnel
   */
  private enrichWithFunnelId(
    event: string,
    properties?: PosthogEventProperties,
  ): PosthogEventProperties {
    const enriched: PosthogEventProperties = { ...properties };

    for (const [funnelName, funnel] of Object.entries(funnelRegistry)) {
      // Check if this is a funnel entry event - generate new ID
      if (funnel.entryEvents.includes(event)) {
        const funnelEntryId = uuidv4();
        this.activeFunnels.set(funnelName, funnelEntryId);
        enriched.funnelEntryId = funnelEntryId;

        if (this.config?.debug) {
          console.log(`[PostHog] Funnel '${funnelName}' started with ID:`, funnelEntryId);
        }
        break;
      }

      // Check if this is a funnel event - attach existing ID
      if (funnel.funnelEvents.includes(event)) {
        const activeFunnelId = this.activeFunnels.get(funnelName);
        if (activeFunnelId) {
          enriched.funnelEntryId = activeFunnelId;

          if (this.config?.debug) {
            console.log(`[PostHog] Funnel '${funnelName}' event with ID:`, activeFunnelId);
          }
        }
        break;
      }
    }

    return enriched;
  }

  /**
   * Track a page view
   */
  trackPageView(path: string, properties?: PosthogEventProperties): void {
    if (!this.isReady()) return;

    try {
      const sanitizedProperties = sanitizePayload({
        ...properties,
        path,
        url: window.location.href,
        referrer: document.referrer,
      });

      this.posthogInstance?.capture('$pageview', sanitizedProperties);

      if (this.config?.debug) {
        console.log('[PostHog] Page view tracked:', path, sanitizedProperties);
      }
    } catch (error) {
      console.error('[PostHog] Page view error:', error);
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.isReady()) return;

    try {
      const sanitizedProperties = sanitizePayload(properties);
      this.posthogInstance?.people?.set(sanitizedProperties);

      if (this.config?.debug) {
        console.log('[PostHog] User properties set:', sanitizedProperties);
      }
    } catch (error) {
      console.error('[PostHog] Set user properties error:', error);
    }
  }

  /**
   * Set person properties on the current profile.
   *
   * `set` overwrites existing values (last-touch); `setOnce` only writes keys
   * that don't already exist on the profile (first-touch). Used for campaign
   * attribution so the acquiring campaign sticks forever while the most recent
   * campaign stays current. Writes to the current profile only — the anonymous
   * person before login (which PostHog stitches into the office on identify) or
   * the logged-in office directly — so a campaign never leaks onto another user
   * on a shared browser.
   */
  setPersonProperties(set?: Record<string, any>, setOnce?: Record<string, any>): void {
    if (!this.isReady()) return;

    try {
      const sanitizedSet = set ? sanitizePayload(set) : undefined;
      const sanitizedSetOnce = setOnce ? sanitizePayload(setOnce) : undefined;

      const hasSet = !!sanitizedSet && Object.keys(sanitizedSet).length > 0;
      const hasSetOnce = !!sanitizedSetOnce && Object.keys(sanitizedSetOnce).length > 0;

      if (!hasSet && !hasSetOnce) return;

      if (hasSet) {
        this.posthogInstance?.people?.set(sanitizedSet);
      }
      if (hasSetOnce) {
        this.posthogInstance?.people?.set_once(sanitizedSetOnce);
      }

      if (this.config?.debug) {
        console.log('[PostHog] Person properties set:', {
          set: sanitizedSet,
          setOnce: sanitizedSetOnce,
        });
      }
    } catch (error) {
      console.error('[PostHog] Set person properties error:', error);
    }
  }

  /**
   * Reset user (on logout)
   */
  reset(): void {
    if (!this.isReady()) return;

    try {
      this.posthogInstance?.reset();
      // Allow flag events to fire again for the next user session.
      this.flagCallReported = {};

      // Re-register super properties and person properties so feature flags
      // targeting environment/appType continue to work for the new anonymous user
      const baseProperties = {
        environment: import.meta.env.VITE_TEMP_MEE_ENV || 'development',
        appType: 'dental_portal',
      };
      this.posthogInstance?.register(baseProperties);
      this.posthogInstance?.people.set(baseProperties);

      // Re-register the onFeatureFlags listener that reset() wiped
      featureFlagManager.handlePosthogReset();

      if (this.config?.debug) {
        console.log('[PostHog] User reset');
      }
    } catch (error) {
      console.error('[PostHog] Reset error:', error);
    }
  }

  /**
   * Start session recording (opt-in for specific flows)
   */
  startSessionRecording(): void {
    if (!this.isReady()) return;

    try {
      this.posthogInstance?.startSessionRecording();

      if (this.config?.debug) {
        console.log('[PostHog] Session recording started');
      }
    } catch (error) {
      console.error('[PostHog] Start session recording error:', error);
    }
  }

  /**
   * Stop session recording
   */
  stopSessionRecording(): void {
    if (!this.isReady()) return;

    try {
      this.posthogInstance?.stopSessionRecording();

      if (this.config?.debug) {
        console.log('[PostHog] Session recording stopped');
      }
    } catch (error) {
      console.error('[PostHog] Stop session recording error:', error);
    }
  }

  /**
   * Check if session recording is active
   */
  isSessionRecordingEnabled(): boolean {
    if (!this.isReady()) return false;

    try {
      return this.posthogInstance?.sessionRecordingStarted() || false;
    } catch (error) {
      console.error('[PostHog] Check session recording error:', error);
      return false;
    }
  }

  /**
   * Get distinct ID of current user
   */
  getDistinctId(): string | undefined {
    if (!this.isReady()) return undefined;
    return this.posthogInstance?.get_distinct_id();
  }

  /**
   * Check if a feature flag is enabled.
   * Reports a $feature_flag_called event at most once per flag key per session.
   */
  isFeatureEnabled(flagKey: string, defaultValue: boolean = false): boolean {
    if (!this.isReady()) return defaultValue;

    try {
      const isEnabled = this.posthogInstance?.isFeatureEnabled(flagKey, { send_event: false });
      const value = isEnabled ?? defaultValue;

      this.reportFlagCalledOnce(flagKey, value);

      return value;
    } catch (error) {
      console.error('[PostHog] Feature flag error:', error);
      return defaultValue;
    }
  }

  /**
   * Get feature flag variant value.
   * Returns the variant string (e.g., 'control', 'test') or boolean if no variants.
   * Reports a $feature_flag_called event at most once per flag key per session.
   */
  getFeatureFlagVariant(flagKey: string): string | boolean | null {
    if (!this.isReady()) return null;

    try {
      // getFeatureFlag returns the variant value when using multivariate flags
      const value = this.posthogInstance?.getFeatureFlag(flagKey, { send_event: false }) ?? null;

      this.reportFlagCalledOnce(flagKey, value);

      return value;
    } catch (error) {
      console.error('[PostHog] Feature flag variant error:', error);
      return null;
    }
  }

  /**
   * Check if a feature flag has a specific variant
   */
  isFeatureFlagVariant(flagKey: string, expectedVariant: string): boolean {
    const variant = this.getFeatureFlagVariant(flagKey);
    return variant === expectedVariant;
  }

  /**
   * Get feature flag payload
   */
  getFeatureFlagPayload(flagKey: string): any {
    if (!this.isReady()) return null;

    try {
      return this.posthogInstance?.getFeatureFlagPayload(flagKey);
    } catch (error) {
      console.error('[PostHog] Feature flag payload error:', error);
      return null;
    }
  }

  /**
   * Reload feature flags from server
   */
  reloadFeatureFlags(): void {
    if (!this.isReady()) return;

    try {
      this.posthogInstance?.reloadFeatureFlags();

      if (this.config?.debug) {
        console.log('[PostHog] Feature flags reloaded');
      }
    } catch (error) {
      console.error('[PostHog] Reload feature flags error:', error);
    }
  }

  /**
   * Wait for the next feature flags response from the server.
   * Use this after an identify call that already triggers a flag reload
   * internally — this avoids a redundant /decide API call.
   */
  async waitForFlagReload(timeoutMs: number = 5000): Promise<void> {
    if (!this.isReady() || !this.posthogInstance) return;

    return new Promise<void>((resolve) => {
      const timeout = setTimeout(resolve, timeoutMs);

      this.posthogInstance!.onFeatureFlags(() => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }

  /**
   * Register a callback for when feature flags are loaded
   */
  onFeatureFlags(
    callback: (flags: string[], variants: Record<string, string | boolean>) => void,
  ): void {
    if (!this.isReady()) return;

    try {
      this.posthogInstance?.onFeatureFlags(callback);
    } catch (error) {
      console.error('[PostHog] Feature flags callback error:', error);
    }
  }

  /**
   * Sends a $feature_flag_called event once per flag key per session,
   * and again if the resolved value changes (e.g. after a server-side toggle).
   * This replaces the SDK's built-in reporting (disabled via send_event: false)
   * to avoid the dedup reset that happens on every flag reload.
   *
   * Uses posthog.featureFlags.getFeatureFlagDetails() to include metadata
   * (flag ID, version, evaluation reason) that the SDK normally attaches.
   *
   * Public so FeatureFlagManager can share the same dedup map instead of
   * maintaining a duplicate one.
   */
  reportFlagCalledOnce(
    flagKey: string,
    value: boolean | string | null,
  ): void {
    const dedupKey =
      typeof value === 'string' ? `variant:${flagKey}` : `bool:${flagKey}`;

    if (
      dedupKey in this.flagCallReported &&
      this.flagCallReported[dedupKey] === value
    ) {
      return;
    }

    this.flagCallReported[dedupKey] = value;

    const details =
      this.posthogInstance?.featureFlags?.getFeatureFlagDetails(flagKey);

    this.posthogInstance?.capture('$feature_flag_called', {
      $feature_flag: flagKey,
      $feature_flag_response: value,
      ...(details?.metadata?.id != null && {
        $feature_flag_id: details.metadata.id,
      }),
      ...(details?.metadata?.version != null && {
        $feature_flag_version: details.metadata.version,
      }),
      ...((details?.reason?.description ?? details?.reason?.code) && {
        $feature_flag_reason:
          details.reason?.description ?? details.reason?.code,
      }),
    });

    if (this.config?.debug) {
      console.log(`[PostHog] Feature flag '${flagKey}':`, value);
    }
  }

  /**
   * Check if PostHog is ready to use
   */
  private isReady(): boolean {
    const ready = this.initialized && this.posthogInstance !== null && !this.config?.disabled;

    if (!ready && this.config?.debug) {
      console.log('[PostHog] Not ready:', {
        initialized: this.initialized,
        hasInstance: this.posthogInstance !== null,
        disabled: this.config?.disabled,
      });
    }

    return ready;
  }

  /**
   * Check if feature flags have been loaded (synchronous check)
   * Returns true if flags are ready, false otherwise
   */
  areFlagsReady(): boolean {
    return this.flagsReady && this.isReady();
  }

  /**
   * Wait for feature flags to be loaded from the server
   * Use this when you need to ensure flags are loaded before checking them
   * to avoid race conditions with default values
   *
   * NOTE: For React components, use the useFeatureFlag hook instead,
   * which handles flag readiness internally.
   *
   * @example
   * // In a saga
   * const posthog = PosthogService.getInstance();
   * yield call([posthog, posthog.waitForFlags]);
   * const isEnabled = posthog.isFeatureEnabled('my-feature');
   *
   * @example
   * // In a thunk or utility function
   * await PosthogService.getInstance().waitForFlags();
   * const isEnabled = PosthogService.getInstance().isFeatureEnabled('my-feature');
   */
  async waitForFlags(): Promise<void> {
    if (!this.isReady()) {
      if (this.config?.disabled) {
        return; // Service is disabled, flags won't load
      }
      throw new Error('[PostHog] Service not initialized');
    }

    if (this.flagsReady) {
      return; // Already ready
    }

    if (this.flagsReadyPromise) {
      await this.flagsReadyPromise;
    }
  }
}

export default PosthogService;
